var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from '@snack-uikit/dropdown';
import { ChevronRightSVG } from '@snack-uikit/icons';
import { ITEM_PREFIXES } from '../../../constants';
import { useCollapseContext, useFocusListContext, useNewListContext, useSelectionContext } from '../../Lists/contexts';
import { ListPrivate } from '../../Lists/ListPrivate';
import { BaseItem } from '../BaseItem';
import { useGroupItemSelection } from '../hooks';
import { extractActiveItems, isNextListItem } from '../utils';
import { FALLBACK_PLACEMENTS } from './constants';
export function NextListItem(_a) {
    var { items, placement = 'right-start', id, scroll, scrollRef, disabled, onSublistOpenChanged, allChildIds, loading = false, focusId = id, untouchableScrollbars = false } = _a, option = __rest(_a, ["items", "placement", "id", "scroll", "scrollRef", "disabled", "onSublistOpenChanged", "allChildIds", "loading", "focusId", "untouchableScrollbars"]);
    const { flattenItems, focusFlattenItems } = useNewListContext();
    const { value, isSelectionSingle, isSelectionMultiple } = useSelectionContext();
    const { openCollapseItems = [] } = useCollapseContext();
    const item = flattenItems[id];
    const { ids, expandedIds } = useMemo(() => {
        const { ids, expandedIds } = extractActiveItems({
            focusCloseChildIds: items,
            focusFlattenItems,
            openCollapseItems,
            isSelectionMultiple,
        });
        return { ids, expandedIds: expandedIds.concat([id]) };
    }, [focusFlattenItems, id, isSelectionMultiple, items, openCollapseItems]);
    const { handleListKeyDownFactory, activeItemId, forceUpdateActiveItemId } = useFocusListContext();
    const [open, setOpen] = useState();
    const handleListKeyDown = useCallback((e) => {
        handleListKeyDownFactory(ids, expandedIds)(e);
        if (e.key === 'ArrowLeft') {
            forceUpdateActiveItemId === null || forceUpdateActiveItemId === void 0 ? void 0 : forceUpdateActiveItemId(focusId);
            setOpen(false);
            e.stopPropagation();
            return;
        }
    }, [handleListKeyDownFactory, ids, expandedIds, forceUpdateActiveItemId, focusId]);
    const { indeterminate, checked: checkedProp, handleOnSelect, } = useGroupItemSelection({
        items: isNextListItem(item) ? item.items : [],
        id,
        disabled,
        allChildIds,
    });
    const handleOutsideClick = useCallback(() => {
        forceUpdateActiveItemId === null || forceUpdateActiveItemId === void 0 ? void 0 : forceUpdateActiveItemId(ITEM_PREFIXES.dropFocus);
        setOpen(false);
        return true;
    }, [forceUpdateActiveItemId]);
    const isOpen = useMemo(() => Boolean(!disabled && activeItemId && focusFlattenItems[focusId].allChildIds.includes(activeItemId)), [activeItemId, disabled, focusFlattenItems, focusId]);
    const checked = Boolean((indeterminate && !open && isSelectionSingle && value && allChildIds.includes(value)) ||
        (isSelectionMultiple && checkedProp));
    useEffect(() => {
        setOpen(open => open && isOpen);
    }, [id, isOpen]);
    const listRef = useRef(null);
    return (_jsx(Dropdown, { outsideClick: handleOutsideClick, fallbackPlacements: FALLBACK_PLACEMENTS, content: _jsx(ListPrivate, { onKeyDown: handleListKeyDown, items: items, nested: true, scroll: scroll, tabIndex: 0, ref: listRef, onFocus: e => {
                e.stopPropagation();
                forceUpdateActiveItemId === null || forceUpdateActiveItemId === void 0 ? void 0 : forceUpdateActiveItemId(ids[0]);
            }, scrollRef: scrollRef, limitedScrollHeight: true, untouchableScrollbars: untouchableScrollbars, loading: loading }), trigger: 'hover', open: isOpen || open, onOpenChange: value => {
            setOpen(value);
            onSublistOpenChanged === null || onSublistOpenChanged === void 0 ? void 0 : onSublistOpenChanged(value, id);
        }, placement: placement, widthStrategy: 'auto', children: _jsx(BaseItem, Object.assign({}, option, { disabled: disabled, open: open, expandIcon: _jsx(ChevronRightSVG, {}), id: id, isParentNode: true, indeterminate: indeterminate, checked: checked, onOpenNestedList: () => {
                setOpen(true);
                setTimeout(() => {
                    var _a;
                    (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 0);
            }, onSelect: handleOnSelect })) }));
}
